/**
 * TestimonialsPage — public-facing testimonials feed for NoJo Enterprise.
 *
 * Data source: `/api/integrations/testimonials` (NoJo proxy → JEC
 * `/api/v1/public/testimonials`). Latest-first, paginated with Load More.
 *
 * Layout mirrors the JTR public testimonials reference:
 *   - Hero headline ("Customer Testimonials" / 「お客様の声」) + total count
 *   - 3-column responsive grid of testimonial cards
 *   - Each card: 5 yellow stars (rating averaged from sub-scores), italic
 *     quote in pastel tinted box, author name (brand purple), green date
 *   - Load More button at the bottom
 *
 * Editorial notes:
 *   - Total is the upstream count of approved-public testimonials.
 *   - Date formatting uses the visitor's locale (Intl).
 *   - Bilingual (EN + JA).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, ShieldCheck, Loader2, MapPin, Building2, Sparkles } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import PageSideAura from "@/components/motion/PageSideAura";
import NBCOlympicsBadge from "@/components/NBCOlympicsBadge";
import Pagination from "@/components/Pagination";
import FloatingPager from "@/components/FloatingPager";
import WhatsNewRoadmapTabs from "@/components/WhatsNewRoadmapTabs";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";
import useTestimonials from "@/lib/useTestimonials";
import { sanitizeField } from "@/lib/sanitizeTestimonial";

const ACCENT = "#5B2E83";
const ACCENT_SOFT = "#F4EEF9";
const DATE_GREEN = "#2F8F3C";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "ja" ? "ja-JP" : "en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).format(d);
  } catch {
    return "";
  }
}

function ratingToStars(rating) {
  // Owner-locked policy (2026-06-21): every approved testimonial
  // displays as 5 stars regardless of the underlying sub-rating data.
  // Customer-submitted sub-scores (customer_service / website /
  // etc_card_use → Excellent / Good / Average / etc.) are still
  // captured upstream for internal analytics, but they no longer
  // influence the public card. The `rating` argument is intentionally
  // ignored — kept for API compatibility with the data shape so a
  // future revert is a one-line change.
  void rating;
  return 5;
}

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className="h-4 w-4"
          style={{ color: i < count ? "#F5B400" : "#E2E8F0" }}
          fill={i < count ? "#F5B400" : "transparent"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ item, lang, index }) {
  const author = item.title || (lang === "ja" ? "お客様" : "Customer");
  const stars = ratingToStars(item.rating);
  const date = formatDate(item.updatedAt, lang);
  // Service-name display correction — upstream JEC ships the legacy
  // domain-style label "JapanCardETC" on each item; the public brand
  // (and the URL we link to from this site) is "JapanETCcard". Normalize
  // here so every card carries the correct brand even if a stale review
  // still carries the old serviceName string.
  const SERVICE_LABEL_MAP = {
    JapanCardETC: "JapanETCcard",
    japancardetc: "JapanETCcard",
    JAPANCARDETC: "JapanETCcard",
  };
  const serviceLabel =
    SERVICE_LABEL_MAP[item.serviceName] ||
    item.serviceName ||
    (item.serviceId === "japantollreceipts"
      ? "JapanTollReceipts"
      : "JapanETCcard");

  // JA translation toggle. When the visitor is on /ja AND the backend
  // attached a JA translation, default to showing the JA text with a
  // small toggle revealing the original English. EN visitors never see
  // the toggle.
  const isJa = lang === "ja";
  const hasOriginal = isJa && item.translated && item.summaryOriginal;
  const [showOriginal, setShowOriginal] = useState(false);
  const quote = hasOriginal && showOriginal ? item.summaryOriginal : item.summary || "";

  // Freshness badges:
  //   • "NEW"    → posted within the last 48 hours
  //   • "RECENT" → posted within the last 7 days (and not already NEW)
  //   • beyond 7 days → no badge
  // (Owner directive — the previous 30-day cutoff was too generous and
  // ended up labelling every visible testimonial as "Recent".)
  const HOUR_MS = 60 * 60 * 1000;
  let freshness = null; // "new" | "recent" | null
  if (item.updatedAt) {
    try {
      const ts = new Date(item.updatedAt).getTime();
      if (Number.isFinite(ts)) {
        const age = Date.now() - ts;
        if (age >= 0 && age < 48 * HOUR_MS) {
          freshness = "new";
        } else if (age >= 0 && age < 7 * 24 * HOUR_MS) {
          freshness = "recent";
        }
      }
    } catch {
      // ignore parse errors
    }
  }

  return (
    <article id={item.id || undefined}>
      <div
        data-testid={`testimonial-card-${item.id || index}`}
        className="h-full flex flex-col rounded-2xl bg-white ring-1 ring-[#E2E8F0] p-5 md:p-6 transition-all hover:-translate-y-0.5 hover:ring-transparent hover:shadow-[0_20px_45px_-25px_rgba(15,29,45,0.3)]"
      >
        {/* Top row: stars left · recent + service tag right */}
        <div className="flex items-start justify-between gap-3">
          <Stars count={stars} />
          <div className="flex items-center gap-1.5 shrink-0">
            {freshness === "new" && (
              <span
                data-testid={`testimonial-card-freshness-new-${item.id || index}`}
                className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.16em] font-mono-display uppercase font-bold px-1.5 py-0.5 rounded-full"
                style={{ color: "#FFFFFF", backgroundColor: "#DC2626" }}
              >
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
                {isJa ? "新着" : "New"}
              </span>
            )}
            {freshness === "recent" && (
              <span
                data-testid={`testimonial-card-freshness-recent-${item.id || index}`}
                className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.16em] font-mono-display uppercase font-bold px-1.5 py-0.5 rounded-full"
                style={{ color: "#B45309", backgroundColor: "#FEF3C7" }}
              >
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
                {isJa ? "最近" : "Recent"}
              </span>
            )}
            <span
              data-testid={`testimonial-card-service-${item.id || index}`}
              className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.18em] font-mono-display uppercase font-bold px-2 py-0.5 rounded-full"
              style={{ color: ACCENT, backgroundColor: ACCENT_SOFT }}
            >
              {serviceLabel}
            </span>
          </div>
        </div>

        <div
          className="mt-4 rounded-xl px-4 py-4"
          style={{ backgroundColor: ACCENT_SOFT }}
        >
          <p className="text-[14px] leading-[1.7] text-[#0F1D2D] italic">
            &ldquo;{quote}&rdquo;
          </p>
          {hasOriginal && (
            <button
              type="button"
              onClick={() => setShowOriginal((p) => !p)}
              data-testid={`testimonial-card-toggle-${item.id || index}`}
              title={
                isJa
                  ? "原文（英語）を表示します。元の英語は、お客様ご本人が直接書かれたものです。"
                  : "Toggle the original English"
              }
              className="mt-3 inline-flex items-center gap-1 text-[10.5px] font-semibold tracking-tight px-2 py-1 rounded-full bg-white ring-1 ring-[#E2E8F0] hover:ring-transparent hover:bg-[#F1F5F9] transition-colors"
              style={{ color: ACCENT }}
            >
              {showOriginal
                ? isJa
                  ? "日本語訳に戻す"
                  : "Show translation"
                : isJa
                  ? "原文（英語）を表示"
                  : "Show original"}
            </button>
          )}
          {isJa && item.translated && (
            <p className="mt-2 text-[10px] text-[#94A3B8] leading-[1.5]">
              ※ お客様ご本人による原文を、AIによって日本語へ翻訳しています。
            </p>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-[#F1F5F9]">
          <div
            className="text-[16px] font-bold tracking-tight"
            style={{ color: ACCENT }}
          >
            {author}
          </div>
          {/* Location block (contract 1.4.0+).
              `branch`   = "US Army" / "US Navy" / "Department Of War"
              `base`     = "Camp Zama" / "CFAY" / "CATC Fuji Camp Fuji"
              `city`     = "Zama" / "Yokosuka" / "Gotemba"
              `command`  = "5th TC" / "USNMRTC Yokosuka" / "Combat Logistics Detachment 341"
              Backend clears `city` when it duplicates `base`. Each row hides
              when its value is empty so 1.3.0 responses still render cleanly. */}
          {(() => {
            /* Sanitize location trio ─ suppress any encrypted (`Enc:`) or
               placeholder values so they never render. Ensures the row
               and the middle-dot separator only appear when both fields
               are legitimately displayable. */
            const branch = sanitizeField(item.branch);
            const base = sanitizeField(item.base);
            const city = sanitizeField(item.city);
            const command = sanitizeField(item.command);
            return (
              <>
                {branch && (
                  <div className="mt-2 flex items-center gap-1.5 text-[12.5px] text-[#475569]">
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0" style={{ color: ACCENT }} strokeWidth={2} />
                    <span className="font-semibold">{branch}</span>
                  </div>
                )}
                {(base || city) && (
                  <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-[#475569]">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-[#DC2626]" strokeWidth={2} />
                    <span>
                      {base}
                      {base && city && <span className="text-[#94A3B8]"> · </span>}
                      {city}
                    </span>
                  </div>
                )}
                {command && (
                  <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-[#475569]">
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-[#0E5FB0]" strokeWidth={2} />
                    <span>{command}</span>
                  </div>
                )}
              </>
            );
          })()}
          {date && (
            <div
              className="mt-2 text-[12.5px] font-semibold tracking-tight"
              style={{ color: DATE_GREEN }}
            >
              {date}
            </div>
          )}
          {/* Share row — X / LinkedIn / Copy Link. Each button deep-links
              to `#testimonial-<id>` on this page so recipients land on
              the exact card. Owner P2 feature (2026-06-21). */}
          {item.id && <ShareRow item={item} lang={lang} author={author} />}
        </div>
      </div>
    </article>
  );
}

function ShareRow({ item, lang, author }) {
  const isJa = lang === "ja";
  // Build a deep-link URL. In SSR-safe environments `window` is undef,
  // so fall back to the canonical corporate host used elsewhere in the
  // codebase (matches SEO.jsx and the sitemap).
  const CANON = "https://nojoenterprise.com";
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : CANON;
  const shareUrl = `${origin}/${lang}/testimonials#${item.id}`;
  const shareText = isJa
    ? `${author} さんからの推薦文 — NoJo Enterprise`
    : `Testimonial from ${author} — NoJo Enterprise`;

  const xHref =
    "https://twitter.com/intent/tweet?" +
    new URLSearchParams({ text: shareText, url: shareUrl }).toString();
  const linkedinHref =
    "https://www.linkedin.com/sharing/share-offsite/?" +
    new URLSearchParams({ url: shareUrl }).toString();

  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Legacy fallback for the rare browser without Clipboard API.
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* silent — clipboard blocked or user cancelled */
    }
  };

  const btnBase =
    "inline-flex items-center justify-center h-7 w-7 rounded-full ring-1 ring-[#E2E8F0] text-[#64748B] hover:text-white transition-colors";
  return (
    <div
      className="mt-3 flex items-center gap-1.5"
      data-testid={`testimonial-card-share-${item.id}`}
      aria-label={isJa ? "この推薦文を共有" : "Share this testimonial"}
    >
      <span className="text-[9.5px] font-mono-display uppercase tracking-[0.18em] text-[#94A3B8] mr-1">
        {isJa ? "共有" : "Share"}
      </span>
      <a
        href={xHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={isJa ? "Xで共有" : "Share on X"}
        data-testid={`testimonial-card-share-x-${item.id}`}
        className={`${btnBase} hover:bg-[#0F1D2D] hover:ring-transparent`}
      >
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M18.244 2H21l-6.52 7.451L22.5 22h-6.828l-5.35-6.99L4.6 22H1.842l6.978-7.978L1.5 2h6.998l4.835 6.394L18.244 2Zm-1.198 18.203h1.86L7.048 3.694H5.06l11.986 16.51Z" />
        </svg>
      </a>
      <a
        href={linkedinHref}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={isJa ? "LinkedInで共有" : "Share on LinkedIn"}
        data-testid={`testimonial-card-share-linkedin-${item.id}`}
        className={`${btnBase} hover:bg-[#0A66C2] hover:ring-transparent`}
      >
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.851-3.037-1.853 0-2.136 1.446-2.136 2.94v5.666H9.353V9h3.414v1.561h.049c.476-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      </a>
      <button
        type="button"
        onClick={onCopy}
        aria-label={
          copied
            ? (isJa ? "コピーしました" : "Copied")
            : (isJa ? "リンクをコピー" : "Copy link")
        }
        data-testid={`testimonial-card-share-copy-${item.id}`}
        className={`${btnBase} hover:bg-[#663399] hover:ring-transparent ${
          copied ? "!text-white !ring-transparent" : ""
        }`}
        style={copied ? { backgroundColor: "#78D33C" } : undefined}
      >
        {copied ? (
          <svg
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default function TestimonialsPage() {
  const { lang = "en" } = useParams();
  const isJa = lang === "ja";

  // URL-reflective pagination — visitors can bookmark / share / reload at
  // a specific page (?page=28 → page 28 of the feed). The `?page=` query
  // string is the source of truth; the hook follows it both on initial
  // mount and on browser back / forward.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = useMemo(() => {
    const raw = parseInt(searchParams.get("page") ?? "1", 10);
    if (!Number.isFinite(raw) || raw < 1) return 0;
    return raw - 1; // hook is 0-indexed; URL is 1-indexed
  }, [searchParams]);

  const { items, total, page, totalPages, loading, error, stale, sourceFreshness, goToPage } =
    useTestimonials({ lang, initialPage: urlPage });

  // Sort toggle — owner P2 feature (2026-06-21). Two modes:
  //   • "recent"  → newest-first (default, matches backend ordering)
  //   • "longest" → sort by summary length, longest quotes first
  // Sort is applied client-side to the CURRENT page only. Users can
  // switch modes without a network round-trip; the paginator still
  // walks the underlying newest-first list from the backend so the
  // total item universe stays stable.
  const [sortMode, setSortMode] = useState("recent");
  const sortedItems = useMemo(() => {
    if (sortMode === "longest") {
      return [...items].sort(
        (a, b) => (b?.summary?.length || 0) - (a?.summary?.length || 0)
      );
    }
    return items;
  }, [items, sortMode]);

  // Deep-link support — when a visitor lands on `/testimonials#testimonial-<id>`
  // scroll to that card once the list has hydrated and briefly highlight
  // it so it's obvious which review the URL was pointing at. Owner P2
  // feature (2026-06-21) — pairs with the share buttons on each card.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#testimonial-")) return;
    if (loading || items.length === 0) return;
    const el = document.getElementById(hash.slice(1));
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-[#663399]", "rounded-2xl");
    const t = setTimeout(() => {
      el.classList.remove("ring-2", "ring-[#663399]", "rounded-2xl");
    }, 2400);
    return () => clearTimeout(t);
  }, [loading, items.length]);

  // Browser back / forward (or external links to `?page=N`) → keep the
  // hook in sync with whatever the URL now says.
  useEffect(() => {
    if (urlPage !== page) {
      goToPage(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage]);

  // Scroll to the grid top when paginating so the user sees the new
  // page from the first card, not where they were mid-scroll on the
  // previous page.
  const gridRef = useRef(null);
  const handlePageChange = useCallback(
    (n) => {
      const clamped = Math.max(0, Math.min(n, Math.max(0, totalPages - 1)));
      // Write the new page to the URL — page 1 stays clean (no `?page=1`),
      // every other page gets a stable, shareable URL.
      const next = new URLSearchParams(searchParams);
      if (clamped === 0) {
        next.delete("page");
      } else {
        next.set("page", String(clamped + 1));
      }
      setSearchParams(next, { replace: false });
      goToPage(clamped);
      if (gridRef.current) {
        // Allow React to update before scrolling
        requestAnimationFrame(() => {
          gridRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    },
    [goToPage, searchParams, setSearchParams, totalPages]
  );

  const formattedTotal = useMemo(() => {
    try {
      return new Intl.NumberFormat(isJa ? "ja-JP" : "en-US").format(total || 0);
    } catch {
      return String(total || 0);
    }
  }, [total, isJa]);

  return (
    <article className="bg-white" data-testid="testimonials-page">
      <SEO {...PAGE_SEO.testimonials[lang]} path={`/${lang}/testimonials`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "お客様の声" : "Testimonials", url: `/${lang}/testimonials` },
        ])}
      />

      {/* ===== TABS (cross-link: What's New / Roadmap / Testimonials) ===== */}
      <WhatsNewRoadmapTabs />

      {/* ===== HERO (condensed). Owner directive Feb 2026 (final): headline
              stays anchored to the left of the row, NBC badge sits on the
              SAME line but horizontally centered to the section (not
              right-anchored). Achieved via absolute centering so the badge
              floats independently of the flex flow. On mobile it falls
              below the title gracefully. ===== */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-5 pb-3 md:py-6">
          <div className="relative flex items-center gap-6 md:gap-10 flex-wrap md:flex-nowrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1
                  data-testid="testimonials-page-h1"
                  className="font-display text-2xl md:text-[30px] font-bold tracking-tight leading-[1.1] text-[#0F1D2D]"
                >
                  {isJa ? "お客様の声" : "Customer Testimonials"}
                </h1>
                {!loading && total > 0 && (
                  <span
                    data-testid="testimonials-total"
                    className="text-[12.5px] font-semibold tracking-tight px-2.5 py-1 rounded-full whitespace-nowrap"
                    style={{ color: ACCENT, backgroundColor: ACCENT_SOFT }}
                  >
                    {isJa
                      ? `${formattedTotal} 件のレビュー`
                      : `${formattedTotal} reviews`}
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-[12.5px] leading-[1.6] text-[#64748B] max-w-2xl">
                {isJa
                  ? "JapanETCcardをご利用のお客様から直接寄せられた声を、新しい順に掲載しています。"
                  : "Real reviews from JapanETCcard customers — newest first."}
              </p>
              <SourceFreshnessBadge
                sourceFreshness={sourceFreshness}
                lang={lang}
                className="mt-2"
              />
            </div>
            {/* NBC badge — desktop: positioned ~60% from the left of the
                row (slightly past center, per owner directive Feb 2026
                "move it a little bit more to the right"). Mobile (<md):
                falls back to static flow centered horizontally with no
                extra bottom space. */}
            <div
              data-testid="testimonials-nbc-wrapper"
              className="w-full md:w-auto flex justify-center mt-2 md:mt-0 md:absolute md:left-[60%] md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:pointer-events-none"
            >
              <div className="md:pointer-events-auto inline-flex">
                <NBCOlympicsBadge lang={lang} placement="row" testId="testimonials-nbc" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SORT TOGGLE — Newest / Longest (P2 backlog, 2026-06-21) ===== */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-end gap-2">
          <span className="text-[10.5px] font-mono-display uppercase tracking-[0.16em] text-[#94A3B8]">
            {isJa ? "並び替え" : "Sort"}
          </span>
          <div
            role="group"
            aria-label={isJa ? "並び替え" : "Sort order"}
            className="inline-flex items-center rounded-full ring-1 ring-[#E2E8F0] bg-white overflow-hidden"
          >
            {[
              { key: "recent", labelEn: "Newest", labelJa: "新着" },
              { key: "longest", labelEn: "Longest", labelJa: "長文" },
            ].map((opt) => {
              const active = sortMode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSortMode(opt.key)}
                  aria-pressed={active}
                  data-testid={`testimonials-sort-${opt.key}`}
                  className={[
                    "px-3.5 py-1.5 text-[11.5px] font-semibold tracking-tight transition-colors",
                    active
                      ? "text-white"
                      : "text-[#475569] hover:text-[#0F1D2D] hover:bg-[#F1F5F9]",
                  ].join(" ")}
                  style={active ? { backgroundColor: "#663399" } : undefined}
                >
                  {isJa ? opt.labelJa : opt.labelEn}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          MIDDLE CONTENT — wrapped with a subtle side aura sitting in
          the page gutters so the purple/blue glow reads on the right
          and left of the central reviews grid, NOT over the hero above
          or the trust band below.
          ═══════════════════════════════════════════════════════════ */}
      <div className="relative isolate" data-testid="testimonials-middle">
        <PageSideAura testId="testimonials-side-aura" />

      {/* ===== GRID ===== */}
      <section ref={gridRef} className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-16">
          {loading && items.length === 0 && (
            <div className="flex items-center justify-center py-20" data-testid="testimonials-loading">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: ACCENT }} />
              <span className="ml-3 text-[14px] text-[#64748B]">
                {isJa ? "レビューを読み込み中…" : "Loading reviews…"}
              </span>
            </div>
          )}

          {error && items.length === 0 && (
            <div
              className="rounded-xl bg-white ring-1 ring-[#E2E8F0] p-8 text-center"
              data-testid="testimonials-error"
            >
              <div className="flex items-center justify-center mb-3">
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: ACCENT }} />
              </div>
              <p className="text-[14px] text-[#475569] leading-relaxed max-w-md mx-auto">
                {isJa
                  ? "現在、ホスト側のデータベースと同期中です。通常2〜3分ほどで自動的に最新のレビューが表示されます。"
                  : "Reviews are syncing with the host database right now. They typically reappear automatically within 2–3 minutes — no need to refresh."}
              </p>
            </div>
          )}

          {/* Soft "showing cached reviews while we re-sync" banner — only
              appears when the upstream call failed but we have cached
              reviews to keep the page useful. */}
          {stale && items.length > 0 && (
            <div
              className="mb-6 rounded-xl ring-1 ring-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
              data-testid="testimonials-stale-banner"
            >
              <Loader2
                className="h-4 w-4 mt-0.5 shrink-0 animate-spin text-amber-700"
                strokeWidth={2}
              />
              <p className="text-[12.5px] leading-[1.65] text-amber-900">
                {isJa
                  ? "ホストデータベースと同期中です。直近のレビューを一時的にキャッシュから表示しています。通常2〜3分以内に最新の内容に切り替わります。"
                  : "Reviews are syncing with the host database. Showing the most recent cached set in the meantime — the live feed usually resumes within 2–3 minutes."}
              </p>
            </div>
          )}

          {items.length > 0 && (
            <ul
              data-testid="testimonials-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
            >
              {sortedItems.map((it, i) => (
                <li key={it.id || i} id={it.id ? `testimonial-${it.id}` : undefined}>
                  <TestimonialCard item={it} lang={lang} index={i} />
                </li>
              ))}
            </ul>
          )}

          {totalPages > 1 && (
            <div className="mt-12 flex flex-col items-center gap-3" data-testid="testimonials-paginator">
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={handlePageChange}
                disabled={loading}
                testIdPrefix="testimonials-pagination"
                accent={ACCENT}
                ariaLabel={isJa ? "ページネーション" : "Testimonials pagination"}
              />
              <p className="text-[11.5px] font-mono-display tracking-[0.16em] uppercase text-[#94A3B8]" data-testid="testimonials-page-meta">
                {isJa
                  ? `${page + 1} / ${totalPages} ページ · ${formattedTotal} 件中 ${(page * 30 + 1).toLocaleString()}–${Math.min((page + 1) * 30, total).toLocaleString()} を表示`
                  : `Page ${page + 1} of ${totalPages} · Showing ${(page * 30 + 1).toLocaleString()}–${Math.min((page + 1) * 30, total).toLocaleString()} of ${formattedTotal}`}
              </p>
            </div>
          )}
          {totalPages > 1 && (
            <FloatingPager
              onPrev={() => handlePageChange(page - 1)}
              onNext={() => handlePageChange(page + 1)}
              atFirst={page === 0}
              atLast={page >= totalPages - 1}
              isJa={isJa}
              accent="#0E5FB0"
              testIdPrefix="testimonials-floating-pager"
            />
          )}
        </div>
      </section>
      </div>
      {/* ═════════ END MIDDLE CONTENT (side aura wrapper closes) ════════ */}

      {/* ===== TRUST BAND =====
          Note: the FederationLiveIndicator used to mount here, but that
          surfaced operational state ("sync paused · pull mode active")
          to public visitors who can't act on it and might misread it
          as a service outage. Now admin-only — see options to expose
          it at a gated `/admin/federation-status` URL below. */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex items-start gap-4">
          <ShieldCheck
            className="h-5 w-5 shrink-0 mt-0.5"
            style={{ color: ACCENT }}
            strokeWidth={1.6}
          />
          <p className="text-[12.5px] leading-[1.85] text-[#475569] max-w-4xl">
            {isJa
              ? "本ページに掲載されているレビューは、JapanETCcardをご利用のお客様から実際に寄せられたものです。NoJo Enterpriseはこれらの声を加工せず、新しい順に表示しています。"
              : "Reviews on this page are submitted directly by real JapanETCcard customers. NoJo Enterprise surfaces them unedited, newest first."}
          </p>
        </div>
      </section>
    </article>
  );
}
